import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface PrintTemplate {
  id: string;
  name: string;
  description: string;
  icon: string;
  type: 'slip' | 'invoice' | 'measurement' | 'report';
  preview: string;
}

interface ExportFormat {
  id: string;
  name: string;
  extension: string;
  icon: string;
  description: string;
}

const PrintExport: React.FC = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [selectedFormat, setSelectedFormat] = useState<string>('pdf');
  const [showPreview, setShowPreview] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Mock print templates
  const printTemplates: PrintTemplate[] = [
    {
      id: 'pickup-slip',
      name: 'Pickup Slip',
      description: 'Simple slip for customer pickup with order details',
      icon: '📦',
      type: 'slip',
      preview: 'Customer pickup confirmation with order details and measurements'
    },
    {
      id: 'detailed-invoice',
      name: 'Detailed Invoice',
      description: 'Professional invoice with shop details and payment terms',
      icon: '🧾',
      type: 'invoice',
      preview: 'Complete invoice with shop logo, customer details, and payment information'
    },
    {
      id: 'measurement-card',
      name: 'Measurement Card',
      description: 'Customer measurement record for future reference',
      icon: '📏',
      type: 'measurement',
      preview: 'Detailed measurement card with customer photo and all measurements'
    },
    {
      id: 'order-summary',
      name: 'Order Summary',
      description: 'Complete order details with timeline and status',
      icon: '📋',
      type: 'report',
      preview: 'Comprehensive order summary with timeline, measurements, and notes'
    },
    {
      id: 'daily-report',
      name: 'Daily Report',
      description: 'Daily summary of orders, deliveries, and revenue',
      icon: '📊',
      type: 'report',
      preview: 'Daily business summary with orders, revenue, and pending work'
    },
    {
      id: 'customer-history',
      name: 'Customer History',
      description: 'Complete customer order history and measurements',
      icon: '👤',
      type: 'report',
      preview: 'Customer profile with complete order history and measurement trends'
    }
  ];

  // Export formats
  const exportFormats: ExportFormat[] = [
    {
      id: 'pdf',
      name: 'PDF',
      extension: '.pdf',
      icon: '📄',
      description: 'Best for printing and sharing'
    },
    {
      id: 'excel',
      name: 'Excel',
      extension: '.xlsx',
      icon: '📊',
      description: 'Good for data analysis'
    },
    {
      id: 'csv',
      name: 'CSV',
      extension: '.csv',
      icon: '📋',
      description: 'Simple data export'
    },
    {
      id: 'word',
      name: 'Word',
      extension: '.docx',
      icon: '📝',
      description: 'Editable document format'
    }
  ];

  const handleGenerateDocument = async () => {
    if (!selectedTemplate) return;
    
    setIsGenerating(true);
    
    // Simulate document generation
    setTimeout(() => {
      setIsGenerating(false);
      setShowPreview(true);
    }, 2000);
  };

  const handlePrint = () => {
    // Simulate print functionality
    window.print();
  };

  const handleDownload = () => {
    // Simulate download functionality
    const link = document.createElement('a');
    link.href = '#';
    link.download = `tailor-document-${Date.now()}.${exportFormats.find(f => f.id === selectedFormat)?.extension}`;
    link.click();
  };

  const renderTemplateCard = (template: PrintTemplate) => (
    <div
      key={template.id}
      onClick={() => setSelectedTemplate(template.id)}
      className={`bg-white rounded-lg shadow hover:shadow-lg transition-all cursor-pointer border-2 ${
        selectedTemplate === template.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
      }`}
    >
      <div className="p-6">
        <div className="flex items-center mb-4">
          <div className="text-3xl mr-3">{template.icon}</div>
          <div>
            <h3 className="font-semibold text-gray-900">{template.name}</h3>
            <p className="text-sm text-gray-600">{template.description}</p>
          </div>
        </div>
        <div className="bg-gray-50 p-3 rounded text-sm text-gray-700">
          <strong>Preview:</strong> {template.preview}
        </div>
      </div>
    </div>
  );

  const renderFormatOption = (format: ExportFormat) => (
    <div
      key={format.id}
      onClick={() => setSelectedFormat(format.id)}
      className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-colors ${
        selectedFormat === format.id
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="text-2xl mr-3">{format.icon}</div>
      <div>
        <h4 className="font-medium text-gray-900">{format.name}</h4>
        <p className="text-sm text-gray-600">{format.description}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/tailor-dashboard" className="text-gray-400 hover:text-gray-600 mr-4">
                <span className="text-2xl">←</span>
              </Link>
              <div className="flex items-center justify-center w-10 h-10 bg-blue-600 rounded-lg">
                <span className="text-white text-lg font-bold">VT</span>
              </div>
              <span className="ml-3 text-xl font-bold text-gray-900">Print & Export</span>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handlePrint}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="text-2xl">🖨️</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">📦</div>
            <h3 className="font-semibold text-gray-900">Print Pickup Slip</h3>
            <p className="text-sm text-gray-600 mb-4">Quick print for customer pickup</p>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              Print Now
            </button>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">🧾</div>
            <h3 className="font-semibold text-gray-900">Generate Invoice</h3>
            <p className="text-sm text-gray-600 mb-4">Professional invoice with details</p>
            <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
              Create Invoice
            </button>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">📊</div>
            <h3 className="font-semibold text-gray-900">Export Data</h3>
            <p className="text-sm text-gray-600 mb-4">Export orders and customers</p>
            <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors">
              Export
            </button>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">📏</div>
            <h3 className="font-semibold text-gray-900">Measurement Card</h3>
            <p className="text-sm text-gray-600 mb-4">Print customer measurements</p>
            <button className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors">
              Print Card
            </button>
          </div>
        </div>

        {/* Template Selection */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Choose Document Template</h2>
            <p className="text-sm text-gray-600">Select a template for your document</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {printTemplates.map(renderTemplateCard)}
            </div>
          </div>
        </div>

        {/* Format Selection */}
        {selectedTemplate && (
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Choose Export Format</h2>
              <p className="text-sm text-gray-600">Select how you want to save the document</p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {exportFormats.map(renderFormatOption)}
              </div>
            </div>
          </div>
        )}

        {/* Generate Button */}
        {selectedTemplate && (
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Generate {printTemplates.find(t => t.id === selectedTemplate)?.name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    Format: {exportFormats.find(f => f.id === selectedFormat)?.name}
                  </p>
                </div>
                <div className="flex space-x-4">
                  <button
                    onClick={handleGenerateDocument}
                    disabled={isGenerating}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isGenerating ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Generating...
                      </>
                    ) : (
                      <>
                        <span className="mr-2">📄</span>
                        Generate Document
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Document Preview</h2>
                    <p className="text-gray-600">
                      {printTemplates.find(t => t.id === selectedTemplate)?.name} - 
                      {exportFormats.find(f => f.id === selectedFormat)?.name}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <span className="text-2xl">×</span>
                  </button>
                </div>

                {/* Preview Content */}
                <div className="bg-gray-50 p-8 rounded-lg mb-6 min-h-[500px]">
                  <div className="bg-white p-8 shadow-sm">
                    {/* Mock document content */}
                    <div className="text-center mb-8">
                      <div className="text-4xl mb-4">👔</div>
                      <h1 className="text-2xl font-bold text-gray-900">Master Tailor Shop</h1>
                      <p className="text-gray-600">Professional Tailoring Services</p>
                    </div>
                    
                    <div className="border-t border-gray-200 pt-6">
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-2">Customer Information</h3>
                          <p className="text-sm text-gray-600">Name: Customer Name</p>
                          <p className="text-sm text-gray-600">Phone: +971 50 123 4567</p>
                          <p className="text-sm text-gray-600">Order: TAILOR-1-0001</p>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-2">Order Details</h3>
                          <p className="text-sm text-gray-600">Type: Wool Suit</p>
                          <p className="text-sm text-gray-600">Price: $299</p>
                          <p className="text-sm text-gray-600">Due: Jan 20, 2024</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-200 pt-6 mt-6">
                      <h3 className="font-semibold text-gray-900 mb-2">Measurements</h3>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Chest: 42"</p>
                          <p className="text-gray-600">Waist: 36"</p>
                          <p className="text-gray-600">Sleeve: 25"</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Neck: 15.5"</p>
                          <p className="text-gray-600">Inseam: 32"</p>
                          <p className="text-gray-600">Height: 5'10"</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Shoulder: 18.5"</p>
                          <p className="text-gray-600">Hip: 42"</p>
                          <p className="text-gray-600">Weight: 160 lbs</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-200 pt-6 mt-6">
                      <h3 className="font-semibold text-gray-900 mb-2">Special Instructions</h3>
                      <p className="text-sm text-gray-600">Customer prefers slim fit with modern styling</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4">
                  <button
                    onClick={handlePrint}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                  >
                    <span className="mr-2">🖨️</span>
                    Print Document
                  </button>
                  <button
                    onClick={handleDownload}
                    className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                  >
                    <span className="mr-2">💾</span>
                    Download
                  </button>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Print Tips */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="mr-2">💡</span>
              Print & Export Tips
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Printing Tips</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Use A4 paper for best results</li>
                  <li>• Enable "Print Background Graphics" for logos</li>
                  <li>• Test print on regular paper first</li>
                  <li>• Keep printer settings at default</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Export Tips</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• PDF is best for sharing and printing</li>
                  <li>• Excel is good for data analysis</li>
                  <li>• CSV is simple for basic data</li>
                  <li>• Word format is editable</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintExport; 